import axios from 'axios';

// One client per mounted mailbox, session secrets stay in memory (not URLs in
// links, localStorage, logs or shared application defaults).
export function createMailboxClient(transport) {
  let session=null,personalSession=null,activeAccountId=null,opening=null,closed=false,epoch=0;
  const ownerToken=localStorage.getItem('token');
  if(!transport){
    transport=axios.create({baseURL:process.env.REACT_APP_API_BASE_URL||'',withCredentials:false,timeout:30000});
    transport.interceptors.request.use(config=>{
      if(!ownerToken||localStorage.getItem('token')!==ownerToken)throw Object.assign(new Error('Account changed'),{code:'MAIL_LOGIN_REQUIRED'});
      config.headers={...config.headers,Authorization:`Bearer ${ownerToken}`};return config;
    });
  }
  const owned=expected=>{if(expected!==undefined&&expected!==epoch)throw Object.assign(new Error('Stale mailbox request'),{code:'ERR_CANCELED'});if(closed||!ownerToken||localStorage.getItem('token')!==ownerToken)throw Object.assign(new Error('Account changed'),{code:'MAIL_LOGIN_REQUIRED'});};
  async function open(){
    owned();
    if(opening)return opening;
    const revision=epoch;
    const work=transport.post('/api/mail/account/session',{previousSessionId:session?.sessionId||null},{timeout:25000})
      .then(async r=>{owned(revision);if(!r.data?.sessionId)throw new Error('Invalid mailbox session');personalSession=r.data;if(!activeAccountId){session=r.data;return session;}const shared=await transport.post(`/api/mail/accounts/${encodeURIComponent(activeAccountId)}/open`,{sessionId:personalSession.sessionId},{timeout:25000});owned(revision);if(!shared.data?.sessionId)throw new Error('Invalid shared mailbox session');session=shared.data;return session;})
      .finally(()=>{if(opening===work)opening=null;});
    opening=work;
    return opening;
  }
  async function call(method,path,data={},signal,blob=false,options={}){
    const revision=epoch;owned(revision);if(!session)await open();owned(revision);
    const invoke=()=>{const multipart=typeof FormData!=='undefined'&&data instanceof FormData;if(multipart)data.set('SessionId',session.sessionId);return transport.request({method,url:'/api/mail/'+path,timeout:options.timeout||30000,signal,...options,
      ...(method==='get'?{params:{...data,sessionId:session.sessionId}}:{data:multipart?data:{...data,sessionId:session.sessionId}}),
      ...(blob?{responseType:'blob'}:{})});};
    try{const result=await invoke();owned(revision);return result.data;}catch(error){
      owned(revision);
      if(error.response?.status===401&&typeof error.response.data?.text==='function'&&error.response.data.size<4096){
        try{error.response.data=JSON.parse(await error.response.data.text());}catch{/* No recognizable session error. */}
      }
      // Never automatically repeat a send/move after an uncertain outcome.
      // A GET may safely obtain a fresh session and retry once.
      if(method==='get'&&error.response?.status===401&&error.response?.data?.code==='MAIL_SESSION_EXPIRED'){
        await open();owned(revision);const result=await invoke();owned(revision);return result.data;
      }
      if(method!=='get'&&error.response?.status===401&&error.response?.data?.code==='MAIL_SESSION_EXPIRED'){
        await open();owned(revision);error.code='MAIL_SESSION_RENEWED';
      }
      throw error;
    }
  }
  return {
    status:async()=>{const revision=epoch;owned();const r=await transport.get('/api/mail/account',{timeout:20000});owned(revision);return r.data;},
    prepare:async()=>{const revision=epoch;owned();const r=await transport.post('/api/mail/account/prepare',{}, {timeout:60000});owned(revision);return r.data;},
    start:()=>{epoch++;closed=false;session=null;personalSession=null;activeAccountId=null;opening=null;},
    open,
    accounts:async()=>{owned();if(!personalSession)await open();owned();const r=await transport.get('/api/mail/accounts',{params:{sessionId:personalSession.sessionId},timeout:20000});return r.data;},
    openAccount:async id=>{owned();if(!personalSession)await open();const revision=epoch;const r=await transport.post(`/api/mail/accounts/${encodeURIComponent(id)}/open`,{sessionId:personalSession.sessionId},{timeout:25000});owned(revision);if(!r.data?.sessionId)throw new Error('Invalid shared mailbox session');activeAccountId=id;session=r.data;return session;},
    openPersonal:()=>{owned();if(!personalSession)throw new Error('Personal mailbox session unavailable');activeAccountId=null;session=personalSession;return session;},
    senders:()=>call('get','senders'),
    folders:signal=>call('get','folders',{},signal),
    profile:()=>call('get','profile'),
    createFolder:(parent,name)=>call('post','folders',{parent,name}),
    rules:()=>call('get','rules'),
    saveRules:(version,rules)=>call('put','rules',{version,rules}),
    messages:(folder,beforeUid,signal)=>call('get','messages',{folder,take:25,...(beforeUid?{beforeUid}:{})},signal),
    search:(q,folder,cursor,signal)=>call('get','search',{q,folder,cursor:cursor||undefined,take:25},signal,false,{timeout:90000}),
    drafts:()=>call('get','drafts'),
    draft:id=>call('get',`drafts/${id}`),
    draftAttachment:(id,fileId)=>call('get',`drafts/${id}/attachments/${fileId}`,{},undefined,true),
    saveDraft:(id,data)=>call('put',`drafts/${id}`,data),
    saveDraftFiles:(id,data,onUploadProgress)=>call('put',`drafts/${id}/files`,data,undefined,false,{timeout:120000,onUploadProgress}),
    discardDraft:(id,version)=>call('delete',`drafts/${id}`,{version}),
    sendDraft:(id,version)=>call('post',`drafts/${id}/send`,{version},undefined,false,{timeout:120000}),
    detail:(folder,uid,signal)=>call('get',`messages/${uid}`,{folder},signal),
    contacts:(q,signal)=>call('get','contacts',{q,take:12},signal),
    read:(folder,uid,isRead)=>call('patch',`messages/${uid}/flags`,{folder,isRead}),
    star:(folder,uid,isStarred)=>call('patch',`messages/${uid}/star`,{folder,isStarred}),
    move:(fromFolder,toFolder,uid)=>call('post',`messages/${uid}/move`,{fromFolder,toFolder}),
    send:data=>call('post','send',data),
    sendFiles:(data,onUploadProgress)=>call('post','send-with-attachments',data,undefined,false,{timeout:120000,onUploadProgress}),
    attachment:(folder,uid,id,maxBytes)=>call('get',`messages/${uid}/attachments/${encodeURIComponent(id)}`,{folder,...(maxBytes?{maxBytes}:{})},undefined,true),
    close:()=>{epoch++;closed=true;session=null;personalSession=null;activeAccountId=null;opening=null;}
  };
}

export function mailboxError(error){
  if(error?.code==='MAIL_SESSION_RENEWED')return 'sessionRenewed';
  const code=error?.response?.data?.code||error?.code;
  if(code==='MAIL_SEND_UNCONFIRMED')return 'sendUnknown';
  if(code==='MAIL_DRAFT_CONFLICT')return 'draftConflict';
  if(code==='MAIL_DRAFT_QUOTA')return 'draftQuota';
  if(code==='MAIL_DRAFT_SETUP_REQUIRED')return 'draftSetup';
  if(code==='MAIL_DRAFT_NOT_FOUND'||code==='MAIL_DRAFT_DISCARDED'||code==='MAIL_DRAFT_SENT')return 'draftGone';
  if(code==='MAIL_ATTACHMENT_INVALID'||error?.response?.status===413)return 'attachmentError';
  if(code==='MAIL_RULES_EXTERNAL')return 'externalRules';
  if(code==='MAIL_RULES_CONFLICT')return 'ruleConflict';
  if(code==='MAIL_SESSION_RENEWED')return 'sessionRenewed';
  if(code==='MAIL_EMPLOYEE_REQUIRED'||code==='MAILBOX_UNAVAILABLE'||code==='MAILBOX_REVIEW_REQUIRED')return 'unavailable';
  if(code==='MAIL_LOGIN_REQUIRED'||error?.response?.status===401)return 'login';
  if(code==='MAIL_INVALID_INPUT'||error?.response?.status===400)return 'invalid';
  return 'serverError';
}
